import React, { useRef, useState, useEffect } from "react";
import moment from "moment";
import Cleave from "cleave.js/react";

import { CalendarBoxContainer } from "./calendar-box-style";
import { images } from "../../../../assets/images";
import NoteText from "../../../components-text/note-text";
import BodyText from "../../../components-text/body-text";
import CalendarItem from "../calendar-item/calendar-item";
import { ERROR_MESSAGES } from "../../../../constants/errorMessages";

interface CalendarBoxProps {
  value: string;
  maxDate?: string;
  minDate?: string;
  required?: boolean;
  disabled?: boolean;
  errorText?: string;
  className?: string;
  removeClearIcon?: boolean;
  alignPopupLeft?: boolean;
  onChange: (value: string) => void;
}

const CalendarBox = ({
  value,
  maxDate,
  minDate,
  required,
  disabled,
  className,
  errorText,
  removeClearIcon,
  alignPopupLeft,
  onChange,
}: CalendarBoxProps) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const calendarIconRef = useRef<HTMLDivElement>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [error, setError] = useState("");

  const handleClickOutside = (event: MouseEvent) => {
    if (
      popupRef.current &&
      !popupRef.current.contains(event.target as Node) &&
      calendarIconRef.current &&
      !calendarIconRef.current.contains(event.target as Node)
    ) {
      setShowCalendar(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getCalendarClassName = () => {
    return [
      className,
      showCalendar && "show-calendar",
      disabled && "disabled",
      (error || errorText) && "error",
      value && "complete",
      required && "required",
    ]
      .filter(Boolean)
      .join(" ");
  };

  const onToggleCalendar = (show: boolean, newValue: string) => {
    setShowCalendar(show);

    if (!newValue || newValue === value) {
      return;
    }

    const dateFormat = "DD/MM/YYYY";
    const parsedData = moment(newValue, dateFormat, true);
    let error = "";

    if (!parsedData.isValid()) {
      error = ERROR_MESSAGES["invalid-date"];
    } else if (minDate && parsedData.isBefore(moment(minDate))) {
      newValue = moment(minDate).format(dateFormat);
    } else if (maxDate && parsedData.isAfter(moment(maxDate))) {
      newValue = moment(maxDate).format(dateFormat);
    }

    setError(error);
    onChange(newValue);
  };

  const onDelete = () => {
    onChange("");
  };

  return (
    <CalendarBoxContainer className={getCalendarClassName()} alignPopupLeft={alignPopupLeft}>
      <div className="input-box-main">
        <div className="input-box-main-field">
          <BodyText>
            <Cleave
              value={value}
              tabIndex={disabled ? -1 : 0}
              onBlur={() => onToggleCalendar(false, value)}
              onFocus={() => onToggleCalendar(false, "")}
              options={{
                date: true,
                delimiter: "/",
                datePattern: ["d", "m", "Y"],
              }}
              onChange={(e) => onToggleCalendar(false, e.target.value)}
            />
          </BodyText>
        </div>

        <div
          className="input-box-icon"
          ref={calendarIconRef}
          onClick={() => {
            onToggleCalendar(!showCalendar, value);
          }}
        >
          <img src={images.IconCalendarLightBlue} alt="icono" />
        </div>

        {!removeClearIcon && (
          <div className="input-box-icon cross" onClick={onDelete}>
            <img src={images.IconCloseBlue} alt="borrar fecha" />
          </div>
        )}
      </div>

      <div className="calendar-popup" ref={popupRef}>
        <CalendarItem
          maxDate={maxDate}
          minDate={minDate}
          onSelect={(newValue, closeCalendar) => {
            onToggleCalendar(!closeCalendar, newValue);
          }}
          date={value}
        />
      </div>

      {(error || errorText) && (
        <div className="input-box-error">
          <NoteText>
            <p>{`* ${error ? error : ""} ${errorText ? errorText : ""}`}</p>
          </NoteText>
        </div>
      )}
    </CalendarBoxContainer>
  );
};

export default CalendarBox;
